import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CreateProductionStageParams,
  ProductionStagesFilterParams,
  ProductionStagesListResponse,
  ProductionStageType,
  UpdateProductionStageParams
} from '@/types/production-stage'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

import {
  bulkDeleteProductionStages,
  createProductionStage,
  deleteProductionStage,
  fetchProductionStage,
  fetchProductionStages,
  sortProductionStage,
  updateProductionStage
} from '@/lib/api'

type ProductionStageStateType = {
  data: ProductionStageType | null
  isLoading: boolean
  error: string | null
}

type ProductionStagesListStateType = {
  data: ProductionStagesListResponse | null
  isLoading: boolean
  error: string | null
}

type ProductionStagesStoreState = {
  productionStage: ProductionStageStateType
  productionStagesList: ProductionStagesListStateType
  filters: ProductionStagesFilterParams
  selectedProductionStageIds: string[]
  setFilters: (filters: Partial<ProductionStagesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  fetchProductionStages: (params?: ProductionStagesFilterParams) => Promise<ProductionStagesListResponse>
  resetProductionStagesList: () => void

  getProductionStage: (id: string) => Promise<ProductionStageType>
  createProductionStage: (params: CreateProductionStageParams) => Promise<ProductionStageType>
  updateProductionStage: (id: string, params: UpdateProductionStageParams) => Promise<ProductionStageType>
  deleteProductionStage: (id: string) => Promise<void>
  bulkDeleteProductionStages: (ids: string[]) => Promise<BulkDeleteResponse>
  sortProductionStage: (id: string, type: SortOrderEnum) => Promise<ProductionStageType>

  selectProductionStage: (id: string) => void
  deselectProductionStage: (id: string) => void
  toggleProductionStageSelection: (id: string) => void
  selectAllProductionStages: (productionStages: ProductionStageType[]) => void
  deselectAllProductionStages: () => void
  isProductionStageSelected: (id: string) => boolean
  resetProductionStage: () => void
}

const defaultFilters: ProductionStagesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useProductionStagesStore = create<ProductionStagesStoreState>()(
  devtools((set, get) => ({
    productionStage: {
      data: null,
      isLoading: false,
      error: null
    },
    productionStagesList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedProductionStageIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof ProductionStagesFilterParams]
          } else {
            updatedFilters[key as keyof ProductionStagesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: ProductionStagesFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as ProductionStagesFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ProductionStagesFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getProductionStage: async id => {
      set(state => ({
        productionStage: { ...state.productionStage, isLoading: true, error: null }
      }))

      try {
        const data = await fetchProductionStage(id)
        set(state => ({ productionStage: { ...state.productionStage, data, isLoading: false } }))
        return data
      } catch (error) {
        set(state => ({
          productionStage: { ...state.productionStage, error: getErrorMessage(error), isLoading: false }
        }))
        throw error
      }
    },

    createProductionStage: async params => {
      set(state => ({
        productionStage: { ...state.productionStage, isLoading: true, error: null }
      }))

      try {
        const data = await createProductionStage(params)
        set(state => ({ productionStage: { ...state.productionStage, data, isLoading: false } }))
        return data
      } catch (error) {
        set(state => ({
          productionStage: { ...state.productionStage, error: getErrorMessage(error), isLoading: false }
        }))
        throw error
      }
    },

    updateProductionStage: async (id, params) => {
      set(state => ({
        productionStage: { ...state.productionStage, isLoading: true, error: null }
      }))

      try {
        const data = await updateProductionStage(id, params)
        set(state => ({ productionStage: { ...state.productionStage, data, isLoading: false } }))
        return data
      } catch (error) {
        set(state => ({
          productionStage: { ...state.productionStage, error: getErrorMessage(error), isLoading: false }
        }))
        throw error
      }
    },

    deleteProductionStage: async id => {
      set(state => ({
        productionStage: { ...state.productionStage, isLoading: true, error: null }
      }))

      try {
        await deleteProductionStage(id)
        set(state => ({ productionStage: { ...state.productionStage, isLoading: false } }))
      } catch (error) {
        set(state => ({
          productionStage: { ...state.productionStage, error: getErrorMessage(error), isLoading: false }
        }))
        throw error
      }
    },

    resetProductionStage: () => {
      set(state => ({
        productionStage: { ...state.productionStage, data: null, error: null }
      }))
    },

    selectProductionStage: id => {
      set(state => {
        if (state.selectedProductionStageIds.includes(id)) {
          return state
        }
        return { selectedProductionStageIds: [...state.selectedProductionStageIds, id] }
      })
    },

    deselectProductionStage: id => {
      set(state => ({
        selectedProductionStageIds: state.selectedProductionStageIds.filter(psId => psId !== id)
      }))
    },

    toggleProductionStageSelection: id => {
      set(state => {
        if (state.selectedProductionStageIds.includes(id)) {
          return { selectedProductionStageIds: state.selectedProductionStageIds.filter(psId => psId !== id) }
        }
        return { selectedProductionStageIds: [...state.selectedProductionStageIds, id] }
      })
    },

    selectAllProductionStages: productionStages => {
      set(() => ({ selectedProductionStageIds: productionStages.map(ps => ps.id) }))
    },

    deselectAllProductionStages: () => {
      set(() => ({ selectedProductionStageIds: [] }))
    },

    isProductionStageSelected: id => {
      return get().selectedProductionStageIds.includes(id)
    },

    bulkDeleteProductionStages: async ids => {
      set(state => ({
        productionStage: { ...state.productionStage, isLoading: true, error: null }
      }))

      try {
        const response = await bulkDeleteProductionStages({ ids })
        set(state => ({
          productionStage: { ...state.productionStage, isLoading: false },
          selectedProductionStageIds: state.selectedProductionStageIds.filter(id => !response.deletedItems.includes(id))
        }))
        return response
      } catch (error) {
        set(state => ({
          productionStage: { ...state.productionStage, error: getErrorMessage(error), isLoading: false }
        }))
        throw error
      }
    },

    fetchProductionStages: async (params?: ProductionStagesFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        productionStagesList: { ...state.productionStagesList, isLoading: true, error: null }
      }))

      try {
        const data = await fetchProductionStages(filtersToUse)
        set(state => ({ productionStagesList: { ...state.productionStagesList, data, isLoading: false } }))
        return data
      } catch (error) {
        set(state => ({
          productionStagesList: {
            ...state.productionStagesList,
            error: getErrorMessage(error),
            isLoading: false
          }
        }))
        throw error
      }
    },

    resetProductionStagesList: () => {
      set(state => ({
        productionStagesList: { ...state.productionStagesList, data: null, error: null }
      }))
    },

    sortProductionStage: async (id, type) => {
      try {
        return await sortProductionStage(id, type)
      } catch (error) {
        throw error
      }
    }
  }))
)

export default useProductionStagesStore
